import React from 'react';
import { connect } from 'react-redux';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// components
import HeadWrapper from 'app/shared/components/HeadWrapper';
import BasicBarChart from 'app/shared/components/Graphs/BasicBarChart';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import ContentBox2 from '@zg-rentals/multibrand-ui/lib/ContentBox2';
import StaticMap from '@zg-rentals/multibrand-ui/lib/StaticMap';

// Actions
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import StaticMapActions from 'app/shared/actions/StaticMapActions';

// Selectors
import { AnalyticsSelectors, AppSelectors } from 'app/shared/selectors';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Audience';
const logger = log.child({ name: 'react' });

const employers = [
    {
        name: 'Amazon',
    },
    {
        name: 'University of Washington',
    },
    {
        name: 'Microsoft',
    },
    {
        name: 'Boeing',
    },
];

const AudiencePage = ({ dispatch, multifamilyListings, isDev }) => {
    const [isLoadingListings, setIsLoadingListings] = React.useState(true);

    /* eslint-disable react-hooks/exhaustive-deps */
    React.useEffect(() => {
        dispatch(AnalyticsActions.getMultifamilyListings())
            .then(() => setIsLoadingListings(false))
            .catch(() => setIsLoadingListings(false));
    }, []);

    const allListings = [
        {
            name: 'Select a Property',
            listingId: '',
        },
        ...multifamilyListings.listings,
    ];

    const signUrl = (url, brand) => {
        const params = { brandName: brand };

        if (isDev) {
            return Promise.resolve(url, brand);
        }
        return dispatch(StaticMapActions.signUrl(url, params))
            .catch(err => logger.error(err));
    };

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <HeadWrapper title={DEFAULT_PAGE_TITLE} />
            <div className="AudiencePage">
                <Row className="AudiencePage-top-row">
                    <Col md={12} lg={5} xl={5}>
                        <Select
                            name="sort"
                            value=""
                            disabled={isLoadingListings}
                        >
                            {allListings.map(listing => (
                                <option key={listing.listingId} value={listing.listingId}>{listing.name}</option>
                            ))}
                        </Select>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Text type="h4" emphasize={true}>Most Popular Zip Codes</Text>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6}>
                        <StaticMap
                            lat={37.756181}
                            lng={-122.421629}
                            markers={[]}
                            scale={2}
                            zoom={12}
                            signUrl={signUrl}
                        />
                    </Col>
                    <Col sm={12} md={6}>
                        <BasicBarChart layout="vertical" />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Text type="h4" emphasize={true}>Zip Codes Renters are moving from</Text>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6}>
                        <StaticMap
                            lat={37.756181}
                            lng={-122.421629}
                            markers={[]}
                            scale={2}
                            zoom={12}
                            signUrl={signUrl}
                        />
                    </Col>
                    <Col sm={12} md={6}>
                        <BasicBarChart layout="vertical" />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Text type="h4" emphasize={true}>Top Employers</Text>
                    </Col>
                </Row>
                <Row className="AudiencePage-top-employers">
                    {employers.map((employer, i) => (
                        <Col sm={12} md={6} lg={3} key={i}>
                            <ContentBox2 className="AudiencePage-employer">
                                <div className="Utils-text-overflow">
                                    {employer.name}
                                </div>
                                <Text type="h2" emphasize={true} className="AudiencePage-employer-count">
                                    {i + 1}
                                </Text>
                            </ContentBox2>
                        </Col>
                    ))}
                </Row>
                <Row>
                    <Col>
                        <Text type="h4" emphasize={true}>Renter Background</Text>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6} lg={3}>
                        <BasicBarChart title="Household Size" />
                    </Col>
                    <Col sm={12} md={6} lg={3}>
                        <BasicBarChart layout="vertical" title="Annual Income" />
                    </Col>
                    <Col sm={12} md={6} lg={3}>
                        <BasicBarChart title="Credit Score" />
                    </Col>
                    <Col sm={12} md={6} lg={3}>
                        <BasicBarChart layout="vertical" title="Est. Affordable Rent" />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Text type="h4" emphasize={true}>Renter Preferences</Text>
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6} lg={4}>
                        <BasicBarChart title="Move in time" />
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                        <BasicBarChart title="Desired Lease Duration" />
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                        <BasicBarChart title="Desired Home type" />
                    </Col>
                </Row>
                <Row>
                    <Col sm={12} md={6} lg={4}>
                        <BasicBarChart title="Pets" />
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                        <BasicBarChart title="Parking" />
                    </Col>
                    <Col sm={12} md={6} lg={4}>
                        <BasicBarChart title="Bedrooms" />
                    </Col>
                </Row>
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    brand: state.app.brand,
    isDev: AppSelectors.makeIsDev(state),
    multifamilyListings: AnalyticsSelectors.makeMultifamilyListings(state),
});

export default withErrorBoundary(connect(mapStateToProps)(AudiencePage));
